import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { colors } from '../../../../../theme/colors';
import { GRID_COLS, GRID_MAX_ROWS, GRID_ROW_HEIGHT, GRID_WIDTH } from '../constants';
import { useGridResize } from '../hooks/useGridResize';
import { GridLayoutItem, TearsheetPanelType, TearsheetV2Layout } from '../types';
import { createOccupancyGrid, GridRect } from '../utils/gridOccupancy';
import { DragPreview } from './DragPreview';
import { PanelSlot } from './PanelSlot';

const CELL_WIDTH = GRID_WIDTH / GRID_COLS;
const CELL_HEIGHT = GRID_ROW_HEIGHT;
const A4_HEIGHT = GRID_MAX_ROWS * GRID_ROW_HEIGHT;

const canvasWrapperStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 1.5rem;
  background: ${colors.neutral[10]};
  overflow: hidden;
`;

const a4ContainerStyle = css`
  width: ${GRID_WIDTH}px;
  height: ${A4_HEIGHT}px;
  background: white;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid ${colors.neutral[20]};
  border-radius: 4px;
  position: relative;
  transform-origin: center center;
`;

const emptyOverlayStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 2px dashed ${colors.neutral[30]};
  border-radius: 4px;
  background: ${colors.neutral[5]};
  pointer-events: none;
  z-index: 1;
`;

const placedItemStyle = css`
  position: absolute;
  box-sizing: border-box;
`;

interface LayoutCanvasProps {
  layout: TearsheetV2Layout;
  isDragging: boolean;
  previewRect: GridRect | null;
  draggingItemId?: string;
  activePanelType?: TearsheetPanelType;
  cursorGridPos?: { x: number; y: number } | null;
  itemSize?: { w: number; h: number };
  onRemovePanel: (itemId: string) => void;
  onResizePanel: (itemId: string, newW: number, newH: number) => void;
  onScaleChange?: (scale: number) => void;
}

// Wrapper component for items that need resize functionality
function ResizablePanelSlot({
  item,
  occupiedCells,
  onRemove,
  onResizeComplete,
}: {
  item: GridLayoutItem;
  occupiedCells: boolean[][];
  onRemove: () => void;
  onResizeComplete: (itemId: string, newW: number, newH: number) => void;
}) {
  const { isResizing, previewSize, handleResizeStart, handleResize, handleResizeEnd } = useGridResize({
    item,
    occupiedCells,
    onResizeComplete,
  });

  return (
    <PanelSlot
      item={item}
      onRemove={onRemove}
      isResizing={isResizing}
      previewSize={previewSize}
      cellWidth={CELL_WIDTH}
      cellHeight={CELL_HEIGHT}
      onResizeStart={handleResizeStart}
      onResize={handleResize}
      onResizeEnd={handleResizeEnd}
    />
  );
}

export const LayoutCanvas = forwardRef<HTMLDivElement, LayoutCanvasProps>(function LayoutCanvas(
  {
    layout,
    isDragging,
    previewRect,
    draggingItemId,
    activePanelType,
    cursorGridPos,
    itemSize,
    onRemovePanel,
    onResizePanel,
    onScaleChange,
  },
  ref
) {
  const hasContent = layout.items.length > 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Create occupancy grid from current layout items (excluding dragged item for resize validation)
  const occupancyGrid = useMemo(
    () => createOccupancyGrid(layout.items, draggingItemId),
    [layout.items, draggingItemId]
  );

  // Calculate scale factor to fit A4 container within available space
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }

    const updateScale = () => {
      const padding = 48; // 1.5rem * 2
      const availableWidth = wrapper.clientWidth - padding;
      const availableHeight = wrapper.clientHeight - padding;

      const scaleX = availableWidth / GRID_WIDTH;
      const scaleY = availableHeight / A4_HEIGHT;
      const newScale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 1

      setScale(newScale);
      onScaleChange?.(newScale);
    };

    updateScale();

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(wrapper);

    return () => resizeObserver.disconnect();
  }, [onScaleChange]);

  return (
    <div ref={wrapperRef} css={canvasWrapperStyle}>
      <div ref={ref} css={a4ContainerStyle} style={{ transform: `scale(${scale})` }}>
        {/* Drag preview */}
        <DragPreview
          rect={previewRect}
          panelType={activePanelType ?? null}
          isDragging={isDragging}
          cursorGridPos={cursorGridPos}
          itemSize={itemSize}
        />

        {/* Placed items */}
        {layout.items.map((item) => (
          <div
            key={item.i}
            css={placedItemStyle}
            style={{
              left: item.x * CELL_WIDTH,
              top: item.y * CELL_HEIGHT,
              width: item.w * CELL_WIDTH,
              height: item.h * CELL_HEIGHT,
            }}
          >
            <ResizablePanelSlot
              item={item}
              occupiedCells={occupancyGrid}
              onRemove={() => onRemovePanel(item.i)}
              onResizeComplete={onResizePanel}
            />
          </div>
        ))}

        {/* Empty state */}
        {!hasContent && !isDragging && (
          <div css={emptyOverlayStyle}>
            <Typography variant='body1' color='text.secondary'>
              Drag a panel here to start
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              Build your tearsheet layout
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
});
